import {
    Banner,
    Button,
    Checkbox,
    COUNTRY_CODES,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ValidatingInput,
    ValidatingSelect,
    ValidationForm,
    ValidationSummary,
} from '@ygtechnologies/bygr-frontend-component-library';
import theme from './AddProfileModal.module.scss';
import React, { useState } from 'react';
import { addProfile, addProfileOptions, checkProfileName } from 'services/profiles';
import { useAsyncState } from 'utils/hooks';
import { getCurrencies } from 'services/currencies';

interface AddProfileModalProps {
    isOpen: boolean;
    toggle(): void;
    onSave(): void;
}

const AddProfileModal: React.FC<AddProfileModalProps> = ({ isOpen, toggle, onSave }) => {
    const [profileOptions, setProfileOptions] = useState<addProfileOptions>({
        isPaying: false,
        name: '',
        currencyId: 1,
        countryCode: 'NO',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [skipNameValidation, setSkipNameValidation] = useState(false);
    const [similarProfileNames, setSimilarProfileNames] = useState<string[]>([]);
    const [currencies] = useAsyncState(() => getCurrencies(), [], null);

    const handleAddProfile = async () => {
        setIsSaving(true);
        try {
            if (!skipNameValidation) {
                let res = await checkProfileName(profileOptions.name);
                if (res.length) {
                    setSimilarProfileNames(res);
                    setSkipNameValidation(true);
                    setIsSaving(false);
                    return;
                }
            }
            await addProfile(profileOptions);
            onSave();
            toggle();
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (changes: Partial<addProfileOptions>, resetNameCheck: boolean = false) => {
        if (resetNameCheck) {
            setSkipNameValidation(false);
        }

        setProfileOptions((prevValue) => ({ ...prevValue, ...changes }));
    };

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader>Add profile</ModalHeader>
            <ValidationForm onSubmit={handleAddProfile}>
                <ModalBody className={theme.body}>
                    <div className="form columns-1">
                        <label>
                            Name
                            <ValidatingInput
                                value={profileOptions.name}
                                required
                                fieldName="Name"
                                placeholder="Name"
                                className="form-control"
                                onChange={(e) => handleChange({ name: e.target.value }, true)}
                            />
                        </label>
                        <label>
                            Country
                            <select
                                className="form-control"
                                value={profileOptions.countryCode}
                                onChange={(e) =>
                                    handleChange({ countryCode: e.target.value as keyof typeof COUNTRY_CODES })
                                }>
                                {Object.entries(COUNTRY_CODES).map(([key, value]) => (
                                    <option key={key} value={key}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {currencies && (
                            <label>
                                Currency
                                <select
                                    className="form-control"
                                    value={profileOptions.currencyId}
                                    onChange={(e) => handleChange({ currencyId: +e.target.value })}>
                                    {currencies.map((x) => (
                                        <option key={x.id} value={x.id}>
                                            {x.name} ({x.code})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <Checkbox
                            checked={profileOptions.isPaying}
                            onChange={(e) => handleChange({ isPaying: e.target.checked })}>
                            Is Paying
                        </Checkbox>
                        {similarProfileNames.length > 0 && (
                            <>
                                <Banner type="warning">
                                    There are Profiles with similar names as the one you want to create. If you still want to
                                    create the profile with this name, save again.
                                </Banner>
                                <Banner type="info">
                                    Profiles with similar names:
                                    <ul>
                                        {similarProfileNames.map((x) => (
                                            <li>{x}</li>
                                        ))}
                                    </ul>
                                </Banner>
                            </>
                        )}
                    </div>
                    <ValidationSummary />
                </ModalBody>
                <ModalFooter>
                    <Button onClick={toggle} color="tertiary">
                        Cancel
                    </Button>
                    <Button loading={isSaving} type="submit" color="primary">
                        Add profile
                    </Button>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default AddProfileModal;
